import { Check, X, Pencil, Sparkles } from 'lucide-react';
import { useMapperSuggestions, useMapperSuggestionsReview } from '../api';
import { useMapperPluginConfig } from '../mapperConfig';
import { useMapStore } from '../MapContext';
import type { MapInfrastructure } from '../types';

interface ReviewTrayProps {
  parcelId?: string | null;
}

/**
 * Agent detect review tray — accept/edit/reject; never auto-commits.
 * Candidates come from the mapper-detection agent (status: 'review') with
 * provenance (source, confidence). Edit opens the infrastructure inspector.
 */
export default function ReviewTray({ parcelId }: ReviewTrayProps) {
  const { data: configData } = useMapperPluginConfig();
  const detectEnabled = configData?.config?.layers?.objects?.detect_enabled;
  const { data } = useMapperSuggestions({ parcel_id: parcelId || undefined, status: 'review' });
  const review = useMapperSuggestionsReview();
  const setSelectedInfrastructure = useMapStore((s) => s.setSelectedInfrastructure);
  const suggestions: MapInfrastructure[] = Array.isArray(data)
    ? data
    : ((data as { suggestions?: MapInfrastructure[] } | undefined)?.suggestions ?? []);

  if (!detectEnabled || suggestions.length === 0) return null;

  const confidenceLabel = (s: MapInfrastructure): string | null => {
    const c = (s.attributes as { confidence?: number } | undefined)?.confidence;
    if (typeof c !== 'number') return null;
    return `${Math.round(c * 100)}%`;
  };

  return (
    <div className="absolute bottom-4 right-4 z-40 w-80 max-h-72 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50/95 p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
          <Sparkles className="h-3.5 h-3.5" />
          Review detected objects
        </h4>
        <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
          {suggestions.length}
        </span>
      </div>
      <ul className="space-y-2">
        {suggestions.map((s) => {
          const conf = confidenceLabel(s);
          return (
            <li key={s.id} className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1.5 text-xs">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-gray-800">{s.name || s.infra_type || s.id}</div>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                  {s.category && <span className="capitalize">{s.category}</span>}
                  {conf && <span>· {conf}</span>}
                  {s.source && <span>· {s.source}</span>}
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  className="rounded p-1 text-green-600 hover:bg-green-50"
                  title="Accept"
                  onClick={() => review.mutate({ ids: [s.id], status: 'active' })}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded p-1 text-gray-500 hover:bg-gray-50"
                  title="Edit (opens inspector)"
                  onClick={() => setSelectedInfrastructure(s)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded p-1 text-red-600 hover:bg-red-50"
                  title="Reject"
                  onClick={() => review.mutate({ ids: [s.id], status: 'rejected' })}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
